import { KeyboardEvent, ReactElement, Ref, forwardRef, useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, Grow, IconButton, Slide, Typography, useMediaQuery } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { alpha, useTheme } from '@mui/material/styles';
import { ArrowRightOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { DISPLAY_FONT, MONO_FONT, RADIUS, REDUCED_MOTION, SR_ONLY_SX } from 'components/ui/tokens';
import { focusRingSx, primarySx as basePrimarySx } from 'components/ui/styleKit';
import { fadeSwap } from 'sections/rooms/rooms.motion';
import { useTourMotion } from 'sections/welcome-tour/motion';
import {
  ACTION_TARGETS,
  CLOSE_METHODS,
  LABEL_TINT_ALPHA,
  SEGMENT_TRACK_ALPHA,
  SPOTLIGHT_STACKED_WIDTH_PX,
  SPOTLIGHT_TRANSITION_MS,
  SPOTLIGHT_WIDE,
  STAGE_ASPECT
} from '../constants';
import { WHATS_NEW_COPY, postDate } from '../content';
import { postIcon } from '../postIcon';
import { postTheme } from '../theme';
import { tourPages } from '../tour';
import type { ActionTarget, CloseMethod, Post, TourPosition } from '../types';
import LabelPill from './LabelPill';
import SpotlightHero from './SpotlightHero';

const c = WHATS_NEW_COPY.popup;
// One popup at a time, so fixed ids are safe.
const TITLE_ID = 'whats-new-title';
const BODY_ID = 'whats-new-body';

const SlideUp = forwardRef(function SlideUp(props: TransitionProps & { children: ReactElement }, ref: Ref<unknown>) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface SpotlightDialogProps {
  // The posts to show, newest first; [] closes the popup.
  posts: Post[];
  onPageShown: (post: Post, at: TourPosition) => void;
  onAction: (post: Post, target: ActionTarget, at: TourPosition) => void;
  onClose: (post: Post, method: CloseMethod, at: TourPosition) => void;
  onSeeAll?: (post: Post, at: TourPosition) => void;
}

/**
 * The launch popup: each post's screens in turn, the picture beside the words (above them on smaller screens, a
 * bottom sheet on phones). One fixed layout and behaviour; each post brings its own colours and pictures.
 * Screens never advance on their own.
 */
const SpotlightDialog = ({ posts, onPageShown, onAction, onClose, onSeeAll }: SpotlightDialogProps) => {
  const muiTheme = useTheme();
  const phone = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const wide = useMediaQuery(muiTheme.breakpoints.up('md'));
  const { reduce, v } = useTourMotion();
  const [index, setIndex] = useState(0);

  // Keep the last posts while the closing animation runs.
  const shown = useRef(posts);
  if (posts.length) shown.current = posts;
  const postsKey = shown.current.map((p) => p.id).join();
  const pages = tourPages(shown.current);
  const current = pages[Math.min(index, pages.length - 1)];
  const open = posts.length > 0;
  const last = index === pages.length - 1;

  // Every opening starts on the first screen. Reset during render, so no effect ever sees the previous screen.
  const [openedFor, setOpenedFor] = useState('');
  if (open && openedFor !== postsKey) {
    setOpenedFor(postsKey);
    setIndex(0);
  }
  if (!open && openedFor) setOpenedFor('');

  const pageShown = useRef(onPageShown);
  pageShown.current = onPageShown;
  const postId = current?.post.id;
  useEffect(() => {
    if (open && current) pageShown.current(current.post, current.at);
    // Keyed on the id: a new array with the same posts is not a new screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, postId, index]);

  if (!current) return null;

  const { post, screen, at } = current;
  const t = postTheme(post, muiTheme);
  const line = alpha(t.text, 0.12);
  const Icon = postIcon(post);
  const tour = at.steps > 1;
  const lastStep = at.step === at.steps - 1;
  const screenKey = `${post.id}:${at.step}`;
  const goTo = (next: number) => setIndex(Math.max(0, Math.min(pages.length - 1, next)));
  const close = (method: CloseMethod) => onClose(post, method, at);
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowRight') goTo(index + 1);
    if (event.key === 'ArrowLeft') goTo(index - 1);
  };

  const quietSx = {
    color: t.textMuted,
    fontWeight: 500,
    borderRadius: RADIUS.control,
    '&:hover': { color: t.text, backgroundColor: alpha(t.text, 0.06) }
  };
  // The app's primary button in this post's colours.
  const primarySx = {
    ...basePrimarySx(muiTheme),
    ...focusRingSx(muiTheme, t.accent),
    whiteSpace: 'nowrap',
    color: t.onAccent,
    backgroundColor: t.accent,
    '&:hover': { backgroundColor: t.accent, color: t.onAccent, boxShadow: `0 8px 22px -10px ${t.glow}`, filter: 'brightness(0.94)' },
    ...(phone && { flex: 1 })
  };
  const arrow = <ArrowRightOutlined aria-hidden />;

  const primary = (() => {
    if (!last) {
      return (
        <Button autoFocus variant="contained" onClick={() => goTo(index + 1)} endIcon={arrow} sx={primarySx}>
          {c.next}
        </Button>
      );
    }
    return post.cta ? (
      <Button autoFocus variant="contained" onClick={() => onAction(post, ACTION_TARGETS.cta, at)} endIcon={arrow} sx={primarySx}>
        {post.cta.label}
      </Button>
    ) : (
      <Button autoFocus variant="contained" onClick={() => close(CLOSE_METHODS.done)} sx={primarySx}>
        {c.done}
      </Button>
    );
  })();

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => close(reason === 'backdropClick' ? CLOSE_METHODS.backdrop : CLOSE_METHODS.esc)}
      TransitionComponent={phone ? SlideUp : Grow}
      transitionDuration={reduce ? 0 : SPOTLIGHT_TRANSITION_MS}
      fullWidth
      maxWidth={false}
      aria-labelledby={TITLE_ID}
      aria-describedby={BODY_ID}
      sx={phone ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
      PaperProps={{
        onKeyDown,
        sx: {
          position: 'relative',
          display: 'flex',
          flexDirection: wide ? 'row' : 'column',
          maxWidth: wide ? SPOTLIGHT_WIDE.width : SPOTLIGHT_STACKED_WIDTH_PX,
          ...(wide && { height: SPOTLIGHT_WIDE.height }),
          borderRadius: RADIUS.panel,
          backgroundImage: 'none',
          // Scrolls when the screen is short (a phone on its side, zoomed text) so the buttons stay reachable.
          overflowX: 'hidden',
          overflowY: 'auto',
          color: t.text,
          backgroundColor: t.surface,
          transition: 'background-color .35s ease, color .35s ease',
          [REDUCED_MOTION]: { transition: 'none' },
          ...(phone && { m: 0, width: '100%', maxWidth: '100%', maxHeight: '94%', borderRadius: `${RADIUS.panel} ${RADIUS.panel} 0 0` })
        }
      }}
    >
      <IconButton
        onClick={() => close(CLOSE_METHODS.close)}
        aria-label={c.close}
        size="small"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 2,
          color: t.text,
          backgroundColor: alpha(t.surface, 0.85),
          border: `1px solid ${line}`,
          '&:hover': { backgroundColor: t.surface }
        }}
      >
        <CloseOutlined aria-hidden />
      </IconButton>

      <Box
        sx={{
          position: 'relative',
          flex: 'none',
          overflow: 'hidden',
          ...(wide
            ? { width: '50%', borderRight: `1px solid ${line}` }
            : { aspectRatio: phone ? STAGE_ASPECT.phone : STAGE_ASPECT.stacked, borderBottom: `1px solid ${line}` })
        }}
      >
        <AnimatePresence exitBeforeEnter initial={false}>
          <Box
            key={screenKey}
            component={motion.div}
            variants={v(fadeSwap)}
            initial="hidden"
            animate="show"
            exit="exit"
            sx={{ position: 'absolute', inset: 0 }}
          >
            <SpotlightHero post={post} media={screen.media} theme={t} />
          </Box>
        </AnimatePresence>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 2.5, sm: 3.5, md: 4 },
          pt: 3,
          pb: phone ? 3 : 2.5,
          ...(wide && { overflowY: 'auto' })
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: wide ? 5 : 0 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 42,
              height: 42,
              flex: 'none',
              display: 'grid',
              placeItems: 'center',
              borderRadius: RADIUS.control,
              color: t.accent,
              backgroundColor: alpha(t.accent, LABEL_TINT_ALPHA),
              fontSize: 22,
              '& svg': { width: 22, height: 22 }
            }}
          >
            <Icon />
          </Box>
          <Box sx={{ minWidth: 0, display: 'grid', gap: 0.375 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', color: t.textMuted, fontSize: 12.5 }}>
              <LabelPill label={post.label} postTheme={t} />
              <span>{post.area}</span>
              <span aria-hidden="true">·</span>
              <span>{postDate(post)}</span>
            </Box>
            {post.feature && <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: t.text }}>{post.feature}</Typography>}
          </Box>
        </Box>

        {pages.length > 1 && (
          <Box aria-hidden="true" sx={{ display: 'flex', gap: 0.75, mt: 2.5 }}>
            {pages.map((p, i) => (
              <Box
                key={`${p.post.id}:${p.at.step}`}
                sx={{
                  flex: 1,
                  height: 4,
                  // A wider gap where the next post's screens start.
                  ml: p.at.page > 0 && p.at.step === 0 ? 1 : 0,
                  borderRadius: RADIUS.pill,
                  backgroundColor: i <= index ? t.accent : alpha(t.text, SEGMENT_TRACK_ALPHA),
                  transition: 'background-color .3s ease',
                  [REDUCED_MOTION]: { transition: 'none' }
                }}
              />
            ))}
          </Box>
        )}

        <AnimatePresence exitBeforeEnter initial={false}>
          <Box
            key={screenKey}
            component={motion.div}
            variants={v(fadeSwap)}
            initial="hidden"
            animate="show"
            exit="exit"
            sx={{ display: 'grid', gap: 1.25, mt: 2.5 }}
          >
            {tour && (
              <Typography
                component="span"
                sx={{
                  fontFamily: MONO_FONT,
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: t.textMuted
                }}
              >
                {c.stepLabel(at.step + 1, at.steps, screen.label)}
              </Typography>
            )}
            <Typography
              id={TITLE_ID}
              component="h2"
              sx={{
                m: 0,
                fontFamily: DISPLAY_FONT,
                fontSize: { xs: 24, md: 28 },
                fontWeight: 600,
                lineHeight: 1.15,
                letterSpacing: '-0.02em'
              }}
            >
              {screen.title}
            </Typography>
            <Typography id={BODY_ID} sx={{ color: t.textMuted, fontSize: { xs: 14.5, md: 15 }, lineHeight: 1.6 }}>
              {screen.body}
            </Typography>
            {screen.chips && (
              <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 0.25, p: 0, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {screen.chips.map((chip) => (
                  <Box
                    component="li"
                    key={chip}
                    sx={{
                      px: 1.25,
                      py: 0.5,
                      borderRadius: RADIUS.pill,
                      fontSize: 12,
                      fontWeight: 500,
                      color: t.text,
                      backgroundColor: alpha(t.text, 0.06)
                    }}
                  >
                    {chip}
                  </Box>
                ))}
              </Box>
            )}
            {!post.steps && post.points && (
              <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 0.5, p: 0, display: 'grid', gap: 0.75 }}>
                {post.points.map((point) => (
                  <Box component="li" key={point} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.125, fontSize: 13.5 }}>
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        flex: 'none',
                        mt: '1px',
                        width: 18,
                        height: 18,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: '50%',
                        fontSize: 10,
                        color: t.accent,
                        backgroundColor: alpha(t.accent, 0.14)
                      }}
                    >
                      <CheckOutlined />
                    </Box>
                    {point}
                  </Box>
                ))}
              </Box>
            )}
            {/* A post's own button stays on its last screen when another post's screens follow. */}
            {post.cta && lastStep && !last && (
              <Box>
                <Button
                  onClick={() => onAction(post, ACTION_TARGETS.cta, at)}
                  endIcon={<ArrowRightOutlined aria-hidden style={{ color: t.accent }} />}
                  sx={{ ...quietSx, color: t.text, fontWeight: 600, px: 1, ml: -1 }}
                >
                  {post.cta.label}
                </Button>
              </Box>
            )}
          </Box>
        </AnimatePresence>

        <Box sx={{ flex: 1, minHeight: 16 }} />

        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, flexWrap: phone ? 'nowrap' : 'wrap' }}
        >
          {index > 0 ? (
            <Button onClick={() => goTo(index - 1)} sx={{ ...quietSx, ml: -1 }}>
              {c.back}
            </Button>
          ) : (
            onSeeAll && (
              <Button onClick={() => onSeeAll(post, at)} sx={{ ...quietSx, px: 1, ml: -1 }}>
                {c.seeAll}
              </Button>
            )
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', ...(phone && { flex: 1 }) }}>
            {post.guideUrl && lastStep && (
              <Button
                component="a"
                href={post.guideUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onAction(post, ACTION_TARGETS.guide, at)}
                sx={quietSx}
              >
                {c.guide}
              </Button>
            )}
            {primary}
          </Box>
        </Box>
      </Box>

      <Box aria-live="polite" sx={SR_ONLY_SX}>
        {pages.length > 1 ? (tour ? c.stepAnnouncement(at.step + 1, at.steps, screen.title) : screen.title) : ''}
      </Box>
    </Dialog>
  );
};

export default SpotlightDialog;
